/**
 * Importance Classification Rules
 * 
 * These rules define how feedback importance is classified on a 3-point scale.
 * The rules are used by the AI to automatically classify feedback importance.
 * 
 * Rules can be edited here to adjust classification behavior.
 */

export type ImportanceLevel = 'High' | 'Medium' | 'Low';

export interface ImportanceRules {
  high: {
    description: string;
    conditions: string[];
  };
  medium: {
    description: string;
    conditions: string[];
  };
  low: {
    description: string;
    conditions: string[];
  };
}

export const IMPORTANCE_RULES: ImportanceRules = {
  high: {
    description: 'Critical - requires immediate attention',
    conditions: [
      'Safety concerns or security issues',
      'Medical emergencies or health-related complaints',
      'Staff misconduct, harassment, or unprofessional behavior',
      'Critical technical failures (no electricity, broken locks, etc.)',
      'Food safety issues',
      'Guest complaints indicating they may leave or request refunds',
      'Very negative sentiment (sentimentScore < 0.2)',
      'Legal or compliance issues',
    ],
  },
  medium: {
    description: 'Important - should be addressed',
    conditions: [
      'Service quality issues affecting guest satisfaction',
      'Moderate technical problems (slow WiFi, minor maintenance)',
      'Food quality or menu-related complaints (not safety-related)',
      'Comfort or cleanliness issues',
      'Staff service quality issues (not misconduct)',
      'Timing or scheduling problems',
      'Moderate negative sentiment (sentimentScore 0.2-0.5)',
    ],
  },
  low: {
    description: 'Minor - nice to have',
    conditions: [
      'Minor suggestions or recommendations',
      'Positive feedback (even with minor suggestions)',
      'General observations without specific complaints',
      'Neutral or positive sentiment (sentimentScore > 0.5)',
      'Cosmetic or aesthetic concerns',
      'Preferences or personal taste differences',
      'Non-urgent feedback',
    ],
  },
};

export const IMPORTANCE_LEVELS: ImportanceLevel[] = ['High', 'Medium', 'Low'];

export const IMPORTANCE_COLORS = {
  High: {
    name: 'High',
    value: 'High',
    colorName: 'Red',
    color: 'text-[#EF4444]',
    statusColor: 'bg-[#fcecec]',
    backColor: 'bg-[#fff7f7] dark:bg-[#963939]',
  },
  Medium: {
    name: 'Medium',
    value: 'Medium',
    colorName: 'Amber',
    color: 'text-[#F59E0B]',
    statusColor: 'bg-[#fdf1db]',
    backColor: 'bg-[#fef9f3] dark:bg-[#b57b19]',
  },
  Low: {
    name: 'Low',
    value: 'Low',
    colorName: 'Blue',
    color: 'text-[#3B82F6]',
    statusColor: 'bg-[#DBEAFE]',
    backColor: 'bg-[#EFF6FF] dark:bg-[#1E40AF]',
  },
};
